import { HTMLAttributes } from "react";
import cx from "classnames";

export type TextInputProps = HTMLAttributes<HTMLInputElement> & {
    disabled?: boolean;
    error?: string;
    leftIcon?: React.Node;
    rightIcon?: React.Node;
    label?: string;
};

export default function TextInput({
    disabled = false,
    error,
    leftIcon: Left,
    rightIcon: Right,
    label,
    description,
    className,
    ...rest
}: TextInputProps) {
    return (
        <div className="flex flex-col gap-2 mt-4 ">
            <div
                className={cx(
                    "relative flex flex-row justify-start items-center self-stretch ring-2 ring-primary-30 focus-within:ring-purple-100 focus-within:ring rounded-lg h-14",
                    className,
                    !!error && "!ring-red-100",
                    !!disabled && "!ring-neutral-40 !text-neutral-50",
                )}>
                <input
                    {...rest}
                    disabled={disabled}
                    placeholder={label}
                    className={cx(
                        "placeholder-transparent outline-none border-none self-stretch rounded-lg py-1 px-4 flex-1 peer w-full",
                        !!Left && "pl-10",
                        !!Right && "pr-10",
                    )}
                />
                {!!label && (
                    <label
                        htmlFor={rest.id}
                        className={cx(
                            `
                            absolute animate transform transition-all text-xs font-semibold
                            -translate-y-7 translate-x-2 bg-white px-1 text-primary-100
                            peer-placeholder-shown:translate-y-0
                            peer-placeholder-shown:text-neutral-50
                            peer-placeholder-shown:cursor-text
                            peer-placeholder-shown:text-base
                            peer-placeholder-shown:font-normal
                            peer-placeholder-shown:select-none
                            peer-focus:-translate-y-7
                            peer-focus:text-primary-100
                            peer-focus:text-xs
                            peer-focus:font-semibold
                            [&:-webkit-autofill]:-translate-y-7
                            [&:-webkit-autofill]:text-primary-100
                            [&:-webkit-autofill]:text-xs
                            [&:-webkit-autofill]:font-semibold
                            autofill:-translate-y-7
                            autofill:text-primary-100
                            autofill:text-xs
                            autofill:font-semibold
                        `,
                            !!error && "!text-red-100 peer-focus:!text-red-100",
                            !!disabled &&
                                "!text-neutral-50 peer-focus:!text-neutral-50",
                        )}>
                        {label}
                    </label>
                )}

                {!!Left && (
                    <div className="pointer-events-none absolute left-0 inset-y-0 flex items-center pl-3">
                        <Left
                            className={cx("h-5 w-5", !!error && "text-red-100")}
                        />
                    </div>
                )}

                {!!Right && (
                    <div className="pointer-events-none absolute right-0 inset-y-0 flex items-center pr-3">
                        <Right
                            className={cx("h-5 w-5", !!error && "text-red-100")}
                        />
                    </div>
                )}
            </div>

            {!error && !!description && (
                <p className="text-sm text-neutral-70 font-poppins">
                    {description}
                </p>
            )}
            {!!error && (
                <p className="text-sm text-red-100 font-poppins">{error}</p>
            )}
        </div>
    );
}
